// Lightweight, privacy-friendly analytics client for Alphalane.
// Stores an anonymous session_id in localStorage, sends events to the backend.
// No third-party trackers, no cookies set.
import axios from "axios";
import { BACKEND_URL } from "./apiBase";

const API = `${BACKEND_URL}/api`;
const SESSION_KEY = "alphalane_session_id";
const SCROLL_KEY = "alphalane_scroll_milestones";

// Only track these public-marketing routes. Everything else (admin, partner,
// dashboard, onboarding, auth, register, …) is intentionally ignored so
// internal sessions don't skew funnel KPIs.
const TRACKED_PATHS_EXACT = new Set(["/", "/blog", "/about"]);
const isTrackedPath = (pathname) => {
  if (typeof pathname !== "string") return false;
  // Strip trailing slash (except for root "/")
  const p = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (TRACKED_PATHS_EXACT.has(p)) return true;
  // Allow blog detail pages: /blog/:slug
  if (p.startsWith("/blog/")) return true;
  return false;
};

const uuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

export const getSessionId = () => {
  if (typeof window === "undefined") return "ssr";
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = uuid();
    localStorage.setItem(SESSION_KEY, id);
    sessionStorage.removeItem(SCROLL_KEY);
  }
  return id;
};

export const track = (name, extra = {}) => {
  if (typeof window === "undefined") return;
  // Skip all tracking on non-public pages (admin, partner, dashboard, etc.).
  // session_end may still fire on transition away from a previously-tracked
  // page, but if the user has navigated to a private page in-between, we
  // simply drop it — counts only public engagement.
  if (!isTrackedPath(window.location.pathname)) return;
  const payload = {
    name,
    session_id: getSessionId(),
    path: window.location.pathname + window.location.hash,
    referrer: document.referrer || null,
    ...extra,
  };
  // Use sendBeacon for reliability when available
  try {
    if (name === "session_end" && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(`${API}/track/event`, blob);
      return;
    }
  } catch {}
  axios.post(`${API}/track/event`, payload).catch(() => {});
};

export const trackPageView = () => track("page_view");

export const initAnalytics = () => {
  if (typeof window === "undefined") return;
  // Initial pageview
  trackPageView();

  // Route changes (hashchange + popstate)
  window.addEventListener("hashchange", trackPageView);
  window.addEventListener("popstate", trackPageView);

  // Scroll depth
  const milestones = [25, 50, 75, 100];
  const seen = new Set(JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "[]"));
  const onScroll = () => {
    const total = document.documentElement.scrollHeight - window.innerHeight;
    if (total <= 0) return;
    const pct = Math.min(100, Math.round((window.scrollY / total) * 100));
    milestones.forEach((m) => {
      if (pct >= m && !seen.has(m)) {
        seen.add(m);
        sessionStorage.setItem(SCROLL_KEY, JSON.stringify([...seen]));
        track("scroll_depth", { meta: { percent: m } });
      }
    });
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  // Track-record viewport detection
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !sessionStorage.getItem("alphalane_tr_seen")) {
          sessionStorage.setItem("alphalane_tr_seen", "1");
          track("track_record_opened");
        }
      });
    },
    { threshold: 0.4 }
  );
  setTimeout(() => {
    const tr = document.querySelector('[data-testid="track-section"]');
    if (tr) observer.observe(tr);
  }, 800);

  // ----- Active time tracking -----
  // Only count seconds while the tab is visible AND the user is active.
  // We pause on visibilitychange→hidden, window blur, and after 60s of
  // inactivity (no mouse/keyboard/scroll/touch). We resume on the next
  // user input or when the tab is focused again.
  let activeMs = 0;
  let activeStart = document.visibilityState === "visible" ? Date.now() : null;
  let idleTimer = null;
  const IDLE_AFTER_MS = 60_000;

  const accumulate = () => {
    if (activeStart != null) {
      activeMs += Date.now() - activeStart;
      activeStart = null;
    }
  };
  const resume = () => {
    if (activeStart == null && document.visibilityState === "visible" && document.hasFocus()) {
      activeStart = Date.now();
    }
  };
  const armIdle = () => {
    if (idleTimer) clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      accumulate();
    }, IDLE_AFTER_MS);
  };
  const onActivity = () => {
    resume();
    armIdle();
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      accumulate();
    } else {
      resume();
      armIdle();
    }
  });
  window.addEventListener("focus", () => { resume(); armIdle(); });
  window.addEventListener("blur", accumulate);
  ["mousemove", "keydown", "scroll", "touchstart", "click"].forEach((evt) => {
    window.addEventListener(evt, onActivity, { passive: true });
  });
  armIdle();

  // Session end on unload — send the ACTIVE duration (clamped at 60 min for
  // backend sanity; the backend also clamps, this is just a courtesy).
  const onUnload = () => {
    accumulate();
    const dur = Math.min(activeMs, 60 * 60 * 1000);
    track("session_end", { duration_ms: dur });
  };
  window.addEventListener("beforeunload", onUnload);
  window.addEventListener("pagehide", onUnload);
};

export const submitLead = async (email, name, opts = {}) => {
  if (!email) return null;
  // Auto-detect the visitor's current site language from the i18n LocalStorage
  // key (set by `LanguageContext`). Falls back to "de".
  let lang = "de";
  try {
    const stored = localStorage.getItem("alphalane_lang");
    if (stored === "en") lang = "en";
  } catch { /* ignore */ }
  try {
    const res = await axios.post(`${API}/track/lead`, {
      email,
      name: name || null,
      phone: opts.phone || null,
      session_id: getSessionId(),
      source: opts.source || "lead_magnet",
      mail_language: lang,
      partner_id: opts.partner_id || null,
      partner_slug: opts.partner_slug || null,
      referrer_name: opts.referrer_name || null,
    });
    track("lead_captured");
    return res.data;
  } catch {
    return null;
  }
};
